import React from "react"
import {
  CustomReasonCode,
  Maybe,
  Order,
  OrderShipping,
  OrderShippingAvardaShippingBroker,
  OrderShippingIngrid,
  OrderShippingKlarnaShippingAssistant,
  OrderShippingNshift,
  OrderShippingQliroIntegratedShipping,
  OrderShippingWalleyDeliveryModule
} from "lib/types/generated/graphql-types"
import OrderBlock from "components/Order/OrderBlock"
import IngridDetails from "./IngridDetails"
import NshiftDetails from "./NshiftDetails"
import AvardaShippingDetails from "./AvardaShippingDetails"
import QliroIntegratedShippingDetails from "./QliroIntegratedShippingDetails"
import KustomShippingAssistantDetails from "./KustomShippingAssistantDetails"
import WalleyDeliveryModuleDetails from "./WalleyDeliveryModuleDetails"
import Address from "../../../../../components/Order/Address/Address"
import { AddressType } from "lib/types/order"
import ShippingDetails from "../../../../../components/Order/Shipping/ShippingDetails"
import {
  getTotalWeight,
  isOrderGiftCardProductRetain24
} from "../../../../../components/Order/helpers"
import ShippingTotals from "../../../../../components/Order/Shipping/ShippingTotals"

import { ReactComponent as DeliveryIcon } from "images/icons/truck-ramp-box.svg"
import { Label } from "./Styled"
import { Deliveries as DeliveriesClass } from "../../../../../classes/order/Deliveries"
import DeliveryComponent from "../../../../../components/Order/Updates/Delivery"
import { Columns } from "../OrderDetails.styled"

type Props = {
  order: Order
  refetch: () => void
  refetchLoading: boolean
  getCustomCode: (code: Maybe<string> | undefined) => CustomReasonCode
}

const Shipping = ({ order, refetch, refetchLoading, getCustomCode }: Props) => {
  const orderShipping = order.Shipping
  const {
    shippingAddress,
    shippingFees,
    orderLines,
    attributes,
    currencyCode,
    date,
    Deliveries,
    gifts,
    GiftCardProduct
  } = order

  const totalWeight = getTotalWeight(orderLines ?? [])

  const deliveries = new DeliveriesClass(Deliveries)

  function isOrderShippingQliroIntegratedShipping(
    object: OrderShipping
  ): object is OrderShippingQliroIntegratedShipping {
    return object.providerName === "QliroIntegratedShipping"
  }

  function isOrderShippingKlarnaIntegratedShipping(
    object: OrderShipping
  ): object is OrderShippingKlarnaShippingAssistant {
    return object.providerName === "KlarnaShippingAssistant"
  }

  function isOrderShippingWalleyDeliveryModuleShipping(
    object: OrderShipping
  ): object is OrderShippingWalleyDeliveryModule {
    return object.providerName === "WalleyDeliveryModule"
  }

  function isOrderShippingAvardaShippingBroker(
    object: OrderShipping
  ): object is OrderShippingAvardaShippingBroker {
    return object.providerName === "AvardaShippingBroker"
  }

  function isOrderShippingIngrid(object: OrderShipping): object is OrderShippingIngrid {
    return object.providerName === "Ingrid"
  }

  function isOrderShippingNshift(object: OrderShipping): object is OrderShippingNshift {
    return object.providerName === "Nshift"
  }

  return (
    <>
      <Columns>
        <Address
          addressType={AddressType.SHIPPING_ADDRESS}
          address={shippingAddress}
          order={order}
          refetch={refetch}
          refetchLoading={refetchLoading}
        />

        <OrderBlock>
          <div>
            <ShippingDetails
              orderShipping={orderShipping}
              shippingFees={shippingFees ?? []}
              totalWeight={totalWeight}
              attributes={attributes}
            />
            <ShippingTotals
              shippingFees={shippingFees}
              currencyCode={currencyCode}
              date={date.toString()}
            />
          </div>
        </OrderBlock>
      </Columns>
      {isOrderShippingIngrid(orderShipping) && <IngridDetails shippingIngrid={orderShipping} />}
      {isOrderShippingNshift(orderShipping) && <NshiftDetails shippingNshift={orderShipping} />}
      {isOrderShippingAvardaShippingBroker(orderShipping) && (
        <AvardaShippingDetails shippingAvarda={orderShipping} />
      )}
      {isOrderShippingQliroIntegratedShipping(orderShipping) && (
        <QliroIntegratedShippingDetails shippingQliroIntegratedShipping={orderShipping} />
      )}
      {isOrderShippingKlarnaIntegratedShipping(orderShipping) && (
        <KustomShippingAssistantDetails shippingKlarna={orderShipping} />
      )}
      {isOrderShippingWalleyDeliveryModuleShipping(orderShipping) && (
        <WalleyDeliveryModuleDetails shippingWalley={orderShipping} />
      )}
      {deliveries?.completedDeliveries().length > 0 && (
        <>
          <Label>
            <DeliveryIcon />
            <h2>Deliveries</h2>
          </Label>
          {deliveries
            ?.completedDeliveries()
            ?.map((delivery) => (
              <DeliveryComponent
                key={delivery.id}
                deliveryId={delivery.id}
                currencyCode={currencyCode}
                orderLines={orderLines ?? []}
                gifts={gifts ?? []}
                shippingFees={shippingFees ?? []}
                getCustomCode={getCustomCode}
                giftCardProducts={
                  isOrderGiftCardProductRetain24(GiftCardProduct)
                    ? GiftCardProduct.giftCardProducts
                    : []
                }
              />
            ))}
        </>
      )}
    </>
  )
}

export default Shipping
